// A column-driven table in the style of Isomorphic's "modern" table variant, adapted from
// design/isomorphic/src/core/data-table.tsx: uppercase muted header on a tinted row, roomy
// cells, hover tint, and columns that drop below a breakpoint so a phone stays readable. A
// column with a `sortValue` sorts the rows when its header is clicked.
import { CaretDown, CaretUp } from "@phosphor-icons/react";
import { type ReactNode, useState } from "react";

import { cn } from "./cn";
import { nextSort, sortRows, type SortState, type SortValue } from "./sort";

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  className?: string;
  /** Hidden below this breakpoint. */
  hideBelow?: "sm" | "md" | "lg" | "xl" | "2xl";
  /** Makes the column sortable by this value. */
  sortValue?: (row: T) => SortValue;
}

const hide = { sm: "hidden sm:table-cell", md: "hidden md:table-cell", lg: "hidden lg:table-cell", xl: "hidden xl:table-cell", "2xl": "hidden 2xl:table-cell" };

function SortButton({ label, state, onClick }: { label: ReactNode; state: SortState["dir"] | undefined; onClick: () => void }) {
  const Icon = state === "asc" ? CaretUp : CaretDown;
  return (
    <button type="button" onClick={onClick} className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900">
      {label}
      <Icon weight="bold" className={cn("h-3 w-3", state ? "text-gray-900" : "text-gray-300")} aria-hidden />
    </button>
  );
}

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  className,
  label,
  defaultSort,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  className?: string;
  label?: string;
  defaultSort?: SortState;
}) {
  const [sort, setSort] = useState(defaultSort);
  const by = columns.find((c) => c.key === sort?.key)?.sortValue;
  const shown = by && sort ? sortRows(rows, by, sort.dir) : rows;
  const onSort = (key: string, value: (row: T) => SortValue) => {
    const first = rows[0];
    setSort((s) => nextSort(s, key, first !== undefined && typeof value(first) === "number"));
  };
  return (
    // `relative`: absolutely placed parts of a cell (a switch's hidden input) scroll and clip with the table.
    <div className={cn("custom-scrollbar relative overflow-x-auto", className)}>
      <table className="w-full border-collapse text-start" aria-label={label}>
        <thead>
          <tr className="bg-gray-100/70 dark:bg-gray-100/60">
            {columns.map((c, i) => {
              const dir = sort?.key === c.key ? sort.dir : undefined;
              const value = c.sortValue;
              return (
                <th
                  key={c.key}
                  aria-sort={value ? (dir === "asc" ? "ascending" : dir === "desc" ? "descending" : "none") : undefined}
                  className={cn(
                    "whitespace-nowrap px-4 py-3 text-start text-[11.5px] font-semibold uppercase tracking-wider text-gray-500",
                    i === 0 && "rounded-s-lg",
                    i === columns.length - 1 && "rounded-e-lg",
                    c.hideBelow && hide[c.hideBelow],
                    c.className,
                  )}
                >
                  {value ? <SortButton label={c.header} state={dir} onClick={() => onSort(c.key, value)} /> : c.header}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {shown.map((r) => (
            <tr key={rowKey(r)} className="border-b border-muted/70 transition-colors last:border-0 hover:bg-gray-50 dark:hover:bg-gray-100/40">
              {columns.map((c) => (
                <td key={c.key} className={cn("px-4 py-3.5 align-middle text-[13.5px] text-gray-700", c.hideBelow && hide[c.hideBelow], c.className)}>
                  {c.cell(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
